import React from 'react';
import PropTypes from 'prop-types';
import {
  getClosestPositionedAncestor,
  getClosestAncestorWithIdOrClass,
} from 'common/utils/dom-util';
import './index.scss';

/**
 * Wrapper to create one/more "sticky" element(s), which can be
 * relative to scrolling on another containing element
 * as indicated by the passed containerSelector prop.
 *
 * NOTE: if DOM changes occur and the Sticky element is moved around,
 * call setDimensions on the element from the parent using a ref,
 * so that Sticky can recalculate it's position
 */
export default class Sticky extends React.Component {
  static propTypes = {
    children: PropTypes.any.isRequired,
    containerSelector: PropTypes.string.isRequired,
    tagName: PropTypes.string,
  };

  static defaultProps = {
    children: null,
    containerSelector: window,
    tagName: 'div',
  };

  /**
   * @param {*} props.children - elements that you desire to stick
   * @param {string|window|document} props.containerSelector - a DOM selector for the scrolling
   *   element that contains the sticky elements (props.children)
   * and/or margin-top that affects when sticky is activated
   * @param {string} props.tagName - if you need a certain tag, such as a thead
   */
  constructor(props) {
    super(props);
    this.state = {};
    this.scrollListener = this.scrollListener.bind(this);
    this.reference = this.reference.bind(this);
  }

  componentDidMount() {
    // Without the setTimeout, the positioning is incorrect, and it's
    // Not clear why (does not have to do with document.readyState)
    setTimeout(() => {
      const {containerSelector} = this.props;

      // Get the container node, whose scrolling will determine stickiness
      this.containerNode = getClosestAncestorWithIdOrClass(
        this.stickyNode,
        containerSelector,
      );

      if (!this.containerNode) {
        throw new Error('cannot find containerSelector ', containerSelector);
      }

      this.setDimensions();
      this.addEventListeners();
    });
  }

  componentWillUnmount() {
    this.removeEventListeners();
  }

  // Performs calculations to determine the topOffset required
  // for the sticky element
  setDimensions() {
    // Scroll to top before getting nodeTop, otherwise, if scrolling
    // Occurs before rendering, the dimensions will be wrong
    this.containerNode.scrollTop = 0;

    // Set dimensions
    this.containerDimensions = this.containerNode.getBoundingClientRect();
    this.nodeDimensions = this.stickyNode.getBoundingClientRect();
    this.closestPositionedAncestorDimensions = getClosestPositionedAncestor(
      this.stickyNode,
    ).getBoundingClientRect();

    // Once mounted and scroll is 0, get the position of the node
    this.nodeTop = this.nodeDimensions.top - this.containerDimensions.top;
  }

  addEventListeners() {
    this.containerNode.addEventListener('scroll', this.scrollListener);
  }

  assignTopOffset() {
    const {containerDimensions, nodeTop, closestPositionedAncestorDimensions} =
      this;

    const {scrollTop} = this.containerNode;

    const shouldLock = scrollTop > nodeTop;

    let imposedOffset = 'auto';
    if (shouldLock) {
      imposedOffset = `${
        scrollTop -
        closestPositionedAncestorDimensions.top +
        containerDimensions.top
      }px`;
    }

    this.setState({
      imposedOffset,
    });
  }

  reference(sticky) {
    this.stickyNode = sticky;
  }

  removeEventListeners() {
    this.containerNode.removeEventListener('scroll', this.scrollListener);
  }

  scrollListener(event) {
    event.stopPropagation();
    this.assignTopOffset();
  }

  render() {
    return React.createElement(
      this.props.tagName,
      {
        className: 'Sticky',
        ref: this.reference,
        style: {
          top: this.state.imposedOffset,
        },
      },
      this.props.children,
    );
  }
}
